import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { logoutAccount } from '../../../actions/logout';
import { HIDE_LOGOUT_WARNING_MODAL } from '../../../actions/ui';
import { SET_UNAUTHORIZED_MESSAGE_VISIBLE } from '../../../actions/login';
import { getLogoutWarningModalShown } from '../../../selectors/ui';
import Modal from '../../Modal';
import { PrimaryButton, SecondaryButton } from '../../buttons';
import './LogoutWarningModal.css';

const TIMEOUT = 60 * 1000;
const SECOND = 1000;

const LogoutWarningModal = props => {
  const { history } = props;
  const dispatch = useDispatch();
  const [countdown, setCountdown] = useState(TIMEOUT);

  const { isOpen, token } = useSelector(state => ({
    isOpen: getLogoutWarningModalShown(state),
    token: state.auth.token
  }));

  // reset timer on open/close
  useEffect(() => {
    setCountdown(TIMEOUT);
  }, [isOpen]);

  const closeModal = useCallback(
    () => dispatch({ type: HIDE_LOGOUT_WARNING_MODAL }),
    [dispatch]
  );

  // sets the isUnauthorizedMessageVisible in state.auth to display the "Session Timed out Message" after logout
  const setUnauthorized = useCallback(
    () => dispatch({ type: SET_UNAUTHORIZED_MESSAGE_VISIBLE }),
    [dispatch]
  );

  const logoutAndClose = useCallback(() => {
    const { dispatchLogout } = bindActionCreators(
      {
        dispatchLogout: logoutAccount
      },
      dispatch
    );
    dispatchLogout(token);
    closeModal();
    history.push('/timed-out');
  }, [closeModal, dispatch, history, token]);

  useEffect(() => {
    if (isOpen) {
      // exit when countdown complete
      if (countdown < SECOND) {
        logoutAndClose();
        return () => {};
      }

      const intervalId = setInterval(() => {
        setCountdown(countdown - SECOND);
      }, SECOND);

      // cleanup interval when effect re-rerun
      return () => {
        clearInterval(intervalId);
      };
    }
    return () => {};
  }, [countdown, isOpen, logoutAndClose, setUnauthorized]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="logout-warning-modal"
      showCloseButton
    >
      <div className="logout-warning-modal__header">
        <h2>Session about to expire</h2>
      </div>
      <div className="logout-warning-modal__body">
        <p>You haven&apos;t done anything for a bit. Are you still there?</p>
        <div className="logout-warning-modal__body-countdown">
          {`00:${moment(countdown).format('mm:ss')}`}
        </div>
        <p>until log out</p>
      </div>
      <div className="logout-warning-modal__footer">
        <SecondaryButton onClick={logoutAndClose}>Log Out</SecondaryButton>
        <PrimaryButton onClick={closeModal}>I&apos;m Here</PrimaryButton>
      </div>
    </Modal>
  );
};

LogoutWarningModal.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

export default LogoutWarningModal;



// WEBPACK FOOTER //
// ./src/components/Modals/LogoutWarningModal/LogoutWarningModal.js